/**
 * Buzz cuts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_named_haircuts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const buzzCutsPack: ToilePack = {
    slug: 'buzz-cuts',
    category: 'beauty',
    name: 'Buzz cuts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'even', label: 'Even buzz', subject: 'an even short buzz cut all over, number 2 length, seen in profile'},
        {key: 'fade', label: 'Buzz fade', subject: 'a buzz cut on top fading to skin at the sides'},
        {key: 'design', label: 'Line design', subject: 'a buzz cut with a thin shaved line design above one ear'},
    ],
    styles: [
        {key: 'barber', label: 'Barber chair', style: 'a barbershop photograph, the man seated in a barber chair with a black cape at the collar, daylight from camera-left through the shop window, a moderate balance with soft shade on the far side of the head', palette: 'Warm wood and cream tile, the black cape, the shop softly blurred behind', framing: 'head and shoulders, eye-level'},
        {key: 'mono', label: 'Black and white', style: 'a black-and-white portrait on fine-grain film against a plain dark grey wall, soft light falling from camera-right, a dark balance with the lit side of the scalp bright against the shadow', palette: 'Monochrome from deep black to clean white, a black T-shirt at the collar', framing: 'head and shoulders, close'},
        {key: 'outdoor', label: 'Outdoors', style: 'a sunlit portrait in a park, bright midday sun from overhead softened by leaves, an airy balance with light dappled shade on the head', palette: 'Leaf green and warm sand, a grey sweatshirt at the collar, the trees blurred behind', framing: 'chest-up, eye-level'},
    ],
    directions: ['bleached buzz', 'with a beard', 'older man'],
    directionHint: 'try: bleached buzz',
    seo: {
        title: 'Buzz Cuts: Even Buzz, Buzz Fade, Line Design',
        metaDescription: 'Nine pictures of the buzz cut at one length: an even buzz, a buzz with a fade and a buzz with a shaved line design, in three settings.',
        h1: 'Buzz cuts: even buzz, buzz fade, line design',
        tagline: 'Nine pictures, three buzz cuts, each in a barber chair, in black and white and outdoors.',
        intro: 'A buzz cut is short, even hair over the whole head. These pictures show an even buzz at number 2 length, a buzz that fades to skin at the sides, and a buzz with a thin shaved line above one ear. Each is shown in a barber chair, in black and white and outdoors. You can type a change, such as a bleached buzz, to ask for the cut with it.',
        useCases: [
            'a reference for the length you want at the barber',
            'choosing between an even buzz, a fade and a line design',
            'a barbershop wall print',
            'a profile picture',
        ],
        faq: [
            {q: 'How do the three buzz cuts differ?', a: 'The even buzz is one length all over. The buzz fade has skin showing at the sides. The line design adds a thin shaved line above one ear.'},
            {q: 'What does number 2 length mean?', a: 'Number 2 is a clipper guard size that sets the length. Ask your barber for a different number if you want the hair longer or shorter.'},
            {q: 'Can I ask for a different colour or an older man?', a: 'Yes. Type the change and the picture is made with it. It is a request, so the result may differ from the sample.'},
        ],
    },
}
